import { toast as sonnerToast } from "sonner";
import type { ReactNode } from "react";
import { IconAlertCircle, IconAlertTriangle, IconCircleCheck, IconInfoCircle } from "@tabler/icons-react";
import { extractApiMessage } from "@/shared/api/extract-message";

export interface ToastOptions {
  title: ReactNode;
  message?: ReactNode;
}

/**
 * Wrapper de `sonner` sobre el mismo shape `{title, message}` que usaban las
 * ~130 llamadas a `notifications.show` de Mantine — migrar un call-site fue
 * cambiar el import, no reescribir la llamada.
 *
 * Estilo "card + acento lateral" (opción B de la revisión UX de
 * notificaciones): superficie neutra del tema (la pone el `<Toaster>` en
 * `__root.tsx` vía `toastOptions.classNames` · bg-card/border-border, se
 * adapta sola al modo oscuro) + barra IZQUIERDA de 3px e ícono Tabler en el
 * color semántico de la paleta. El color del borde va por CLASE literal por
 * tipo (`!border-l-exito-600`, etc.): las utilidades de color POR LADO salen
 * DESPUÉS de las omni-lado en el CSS de Tailwind, así que a igual
 * `!important` le ganan al `!border-border` base del Toaster (un style
 * inline NO le ganaría — inline sin important pierde contra un stylesheet
 * con important). Duraciones por tipo: los errores duran el doble que los
 * éxitos — hay que alcanzar a leerlos (todos traen botón de cerrar).
 */
function mostrar(
  tipo: "success" | "error" | "warning" | "info",
  { title, message }: ToastOptions,
  acentoClass: string,
  icono: ReactNode,
  duration: number,
) {
  sonnerToast[tipo](title, {
    description: message,
    icon: icono,
    duration,
    className: acentoClass,
  });
}

/*
 * Acentos en la FAMILIA DEL NARANJA de marca (pedido del usuario · iteración
 * final: nada de colores ajenos, solo la rampa `multiva` y su vecino dorado).
 * La diferenciación entre tipos es por TONO dentro de la familia + la forma
 * del ícono (✓ / ✕ / ▲ / ℹ), que es quien carga la semántica:
 *   éxito       naranja marca   multiva-600 #ef5423 (el naranja del header)
 *   info        durazno suave   multiva-400 #f9835b (más claro = informativo)
 *   advertencia dorado          ambar-300  #ffb528 (análogo inmediato)
 *   error       café rojizo     multiva-900 #7f280a (el tono más profundo y
 *               grave de la propia rampa — el "rojo" de la familia naranja)
 */
export function toastExito({ title, message }: ToastOptions) {
  mostrar("success", { title, message }, "!border-l-multiva-600", <IconCircleCheck size={16} className="text-multiva-600" />, 4000);
}

export function toastError({ title, message }: ToastOptions) {
  mostrar("error", { title, message }, "!border-l-multiva-900", <IconAlertCircle size={16} className="text-multiva-900" />, 8000);
}

export function toastAdvertencia({ title, message }: ToastOptions) {
  mostrar("warning", { title, message }, "!border-l-ambar-300", <IconAlertTriangle size={16} className="text-ambar-300" />, 6000);
}

export function toastInfo({ title, message }: ToastOptions) {
  mostrar("info", { title, message }, "!border-l-multiva-400", <IconInfoCircle size={16} className="text-multiva-400" />, 5000);
}

/**
 * Reemplazo directo de `shared/api/notificar-error.ts` (el `onError` default
 * de mutations) · misma firma de un solo argumento para servir como
 * `onError` de TanStack Query v5 sin wrapping.
 */
export function toastErrorApi(err: unknown) {
  toastError({ title: "Error", message: extractApiMessage(err) });
}
